import { z } from 'zod';
import { uuidSchema } from './common.js';
import { businessManagerFilterSchema } from './records.js';

/**
 * Reporting contracts.
 *
 * Every figure crossing this boundary is a decimal string, not a JSON number —
 * a revenue total that is a cent out because of float arithmetic is a number
 * nobody can reconcile against their finance system.
 */
export const reportRangeSchema = z
  .object({
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    businessManagerId: businessManagerFilterSchema.optional(),
    pipelineId: uuidSchema.optional(),
  })
  .refine((value) => !value.from || !value.to || value.to >= value.from, {
    message: '`to` must not be before `from`',
    path: ['to'],
  });
export type ReportRange = z.infer<typeof reportRangeSchema>;

export interface PipelineByStageRow {
  stageId: string;
  stageName: string;
  color: string | null;
  position: number;
  count: number;
  value: string;
  weightedValue: string;
}

export interface SalesByRepRow {
  businessManagerId: string | null;
  ownerName: string;
  wonCount: number;
  wonValue: string;
  openCount: number;
  openValue: string;
  /** Percentage of decided deals that were won. */
  winRate: number;
}

export interface ConversionFunnelRow {
  stage: string;
  count: number;
  /** Percentage of the first stage that reached this one. */
  percentOfTop: number;
}

export interface ForecastMonthRow {
  /** `YYYY-MM`. */
  month: string;
  openValue: string;
  weightedValue: string;
  wonValue: string;
  dealCount: number;
}

export interface ActivityVolumeRow {
  businessManagerId: string | null;
  ownerName: string;
  calls: number;
  meetings: number;
  tasks: number;
  completed: number;
  total: number;
}

export interface LeadSourceRow {
  source: string;
  total: number;
  converted: number;
  conversionRate: number;
}

export interface DashboardReport {
  currency: string;
  range: { from: string | null; to: string | null };
  pipelineByStage: PipelineByStageRow[];
  salesByRep: SalesByRepRow[];
  conversionFunnel: ConversionFunnelRow[];
  forecastByMonth: ForecastMonthRow[];
  activityVolume: ActivityVolumeRow[];
  leadSources: LeadSourceRow[];
  totals: {
    openValue: string;
    weightedValue: string;
    wonValue: string;
    lostValue: string;
    openCount: number;
    winRate: number;
  };
}

export const EXPORT_DATASETS = ['leads', 'accounts', 'contacts', 'deals', 'activities'] as const;
export type ExportDataset = (typeof EXPORT_DATASETS)[number];

export const exportQuerySchema = z.object({
  dataset: z.enum(EXPORT_DATASETS),
  /** Caps the export so one click cannot pull a million rows into memory. */
  limit: z.coerce.number().int().min(1).max(10_000).default(5000),
});
export type ExportQuery = z.infer<typeof exportQuerySchema>;
